// Photo list state for Flohmarkt ads and Mitteilungen: upload with real progress, remove, move.
import { useEffect, useRef, useState } from 'react';
import { BASE, mediaUrl } from './lib/api';
import { useToast } from './hooks';

export const MAX_PHOTOS = 6;

/** One photo: existing (image id), uploaded (upload id) or still uploading (progress). */
interface Tile {
  key: string;
  image?: number;
  upload?: string;
  src: string; // preview: media path or local blob URL
  progress?: number; // 0–100 while uploading
}

/** Uploads one file with real progress (XMLHttpRequest – fetch has no upload progress). */
function uploadFile(file: File, onProgress: (p: number) => void) {
  return new Promise<{ id: string; thumb: string }>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${BASE}/api/uploads`);
    xhr.setRequestHeader('X-Requested-With', 'sportplaner');
    xhr.upload.onprogress = e => e.lengthComputable && onProgress(Math.round((e.loaded / e.total) * 100));
    xhr.onload = () => {
      let data: { id?: string; thumb?: string; error?: string } = {};
      try {
        data = JSON.parse(xhr.responseText) as typeof data;
      } catch {
        /* not JSON */
      }
      if (xhr.status >= 200 && xhr.status < 300 && data.id) resolve({ id: data.id, thumb: data.thumb! });
      else reject(new Error(data.error || 'Foto-Upload fehlgeschlagen.'));
    };
    xhr.onerror = () => reject(new Error('Keine Verbindung – Foto-Upload fehlgeschlagen.'));
    const form = new FormData();
    form.append('file', file);
    xhr.send(form);
  });
}

export function usePhotos(initial: { id: number; thumb: string }[] = []) {
  const toast = useToast();
  const [tiles, setTiles] = useState<Tile[]>(() =>
    initial.map(i => ({ key: `i${i.id}`, image: i.id, src: mediaUrl(i.thumb)! })),
  );
  const count = useRef(tiles.length); // photos incl. those just added – two quick drops come before a re-render
  const blobs = useRef<string[]>([]);
  useEffect(() => () => blobs.current.forEach(u => URL.revokeObjectURL(u)), []); // free local previews

  function addFiles(files: FileList | File[]) {
    const images = [...files].filter(f => f.type.startsWith('image/') || /\.(heic|heif)$/i.test(f.name));
    const room = Math.max(0, MAX_PHOTOS - count.current);
    const list = images.slice(0, room);
    if (images.length > room) toast(`Höchstens ${MAX_PHOTOS} Fotos.`);
    count.current += list.length;
    for (const f of list) {
      const key = `u${Math.random().toString(36).slice(2)}`;
      const preview = URL.createObjectURL(f);
      blobs.current.push(preview);
      setTiles(t => [...t, { key, src: preview, progress: 0 }]);
      const set = (patch: Partial<Tile>) => setTiles(t => t.map(x => (x.key === key ? { ...x, ...patch } : x)));
      uploadFile(f, p => set({ progress: p }))
        .then(r => set({ upload: r.id, progress: undefined }))
        .catch((e: Error) => {
          toast(e.message);
          count.current -= 1;
          setTiles(t => t.filter(x => x.key !== key));
        });
    }
  }
  const remove = (key: string) => {
    count.current -= 1;
    setTiles(t => t.filter(x => x.key !== key));
  };
  const move = (i: number, d: -1 | 1) =>
    setTiles(t => {
      const n = [...t];
      [n[i], n[i + d]] = [n[i + d], n[i]];
      return n;
    });
  return {
    tiles,
    addFiles,
    remove,
    move,
    uploading: tiles.some(t => t.progress !== undefined),
    /** final order for the server: existing photos and new uploads */
    payload: () => tiles.map(t => (t.image ? { image: t.image } : { upload: t.upload! })),
  };
}
